import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  alpha,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import {
  Description as DataIcon,
} from '@mui/icons-material';
import {
  Close as CloseIcon,
  Visibility as PreviewIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateIcon,
  OpenInNew as OpenNewIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { getDocumentPreview } from '../../services/api';

interface DocumentPreviewProps {
  /** Local File object (for before-upload preview) */
  file?: File | null;
  /** Firebase document ID (for after-upload / dropdown preview) */
  firebaseDocId?: string | null;
  /** Firebase filename for display */
  firebaseFilename?: string | null;
  /** Compact mode: just show an icon button */
  compact?: boolean;
  /** Custom button label */
  buttonLabel?: string;
}

export default function DocumentPreview({
  file,
  firebaseDocId,
  firebaseFilename,
  compact = false,
  buttonLabel,
}: DocumentPreviewProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firebaseUrl, setFirebaseUrl] = useState<string | null>(null);
  const [firebaseContentType, setFirebaseContentType] = useState<string>('application/pdf');
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [fallbackData, setFallbackData] = useState<any>(null);

  // Determine if we have anything to preview
  const hasPreview = !!(file || firebaseDocId);

  // Local file preview URL - created on dialog open, revoked on close
  const [localUrl, setLocalUrl] = useState<string | null>(null);

  // Determine content type
  const getContentType = (filename: string): string => {
    const lower = filename.toLowerCase();
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.gif')) return 'image/gif';
    if (lower.endsWith('.webp')) return 'image/webp';
    if (lower.endsWith('.bmp')) return 'image/bmp';
    if (lower.endsWith('.tiff') || lower.endsWith('.tif')) return 'image/tiff';
    return 'application/pdf';
  };

  const contentType = file
    ? getContentType(file.name)
    : firebaseContentType !== 'application/pdf'
      ? firebaseContentType
      : firebaseFilename
        ? getContentType(firebaseFilename)
        : 'application/pdf';

  const isImage = contentType.startsWith('image/');
  const isPdf = contentType === 'application/pdf';
  const displayName = file?.name || firebaseFilename || 'Document';

  const handleOpen = async () => {
    setOpen(true);
    setError(null);
    setZoom(100);
    setRotation(0);

    // Create a fresh object URL for local files
    if (file) {
      const url = URL.createObjectURL(file);
      setLocalUrl(url);
    }

    // If it's a Firebase document, fetch the signed URL
    if (firebaseDocId && !file) {
      setLoading(true);
      setFallbackData(null);
      try {
        const preview = await getDocumentPreview(firebaseDocId);
        if (preview.file_unavailable) {
          // File not in storage — show extracted data instead
          setFallbackData(preview.fallback_data);
        } else {
          setFirebaseUrl(preview.signed_url);
        }
        setFirebaseContentType(preview.content_type);
      } catch (err: any) {
        setError(err?.response?.data?.detail || err?.message || 'Failed to load preview');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Revoke the object URL to free memory
    if (localUrl) {
      URL.revokeObjectURL(localUrl);
      setLocalUrl(null);
    }
    setFirebaseUrl(null);
    setFallbackData(null);
  };

  // Also clean up on unmount
  useEffect(() => {
    return () => {
      if (localUrl) {
        URL.revokeObjectURL(localUrl);
      }
    };
  }, [localUrl]);

  const previewUrl = localUrl || firebaseUrl;

  if (!hasPreview) return null;

  return (
    <>
      {/* Preview Trigger Button */}
      {compact ? (
        <Tooltip title={`Preview ${displayName}`}>
          <IconButton
            size="small"
            onClick={handleOpen}
            sx={{
              color: '#8B5CF6',
              backgroundColor: alpha('#8B5CF6', 0.08),
              '&:hover': { backgroundColor: alpha('#8B5CF6', 0.16) },
              transition: 'all 0.2s ease',
            }}
          >
            <PreviewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="outlined"
          size="small"
          startIcon={<PreviewIcon />}
          onClick={handleOpen}
          sx={{
            borderColor: alpha('#8B5CF6', 0.4),
            color: '#8B5CF6',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#8B5CF6',
              backgroundColor: alpha('#8B5CF6', 0.08),
            },
          }}
        >
          {buttonLabel || 'Preview'}
        </Button>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            height: '85vh',
            maxHeight: '85vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1.5,
            px: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            background: `linear-gradient(135deg, ${alpha('#8B5CF6', 0.05)} 0%, ${alpha('#3B82F6', 0.05)} 100%)`,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FileIcon sx={{ fontSize: 18, color: '#fff' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
              {displayName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.25 }}>
              <Chip
                label={isImage ? 'Image' : 'PDF'}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  backgroundColor: alpha(isImage ? '#10B981' : '#3B82F6', 0.12),
                  color: isImage ? '#10B981' : '#3B82F6',
                }}
              />
              {file && (
                <Chip
                  label={`${(file.size / 1024).toFixed(1)} KB`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
              {firebaseDocId && !file && (
                <Chip
                  label="From Firebase"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
                    color: '#fff',
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Toolbar */}
          {isImage && previewUrl && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Zoom Out">
                <IconButton size="small" onClick={() => setZoom((z) => Math.max(25, z - 25))} disabled={zoom <= 25}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Chip
                label={`${zoom}%`}
                size="small"
                variant="outlined"
                sx={{ height: 24, fontSize: '0.7rem', minWidth: 48 }}
              />
              <Tooltip title="Zoom In">
                <IconButton size="small" onClick={() => setZoom((z) => Math.min(300, z + 25))}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rotate">
                <IconButton size="small" onClick={() => setRotation((r) => (r + 90) % 360)}>
                  <RotateIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {previewUrl && (
            <Tooltip title="Open in new tab">
              <IconButton
                size="small"
                onClick={() => window.open(previewUrl, '_blank')}
                sx={{ color: 'text.secondary' }}
              >
                <OpenNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: alpha('#EF4444', 0.1), color: '#EF4444' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha('#000', 0.03),
            overflow: 'auto',
          }}
        >
          {loading && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress size={48} sx={{ color: '#8B5CF6', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading document preview...
              </Typography>
            </Box>
          )}

          {error && (
            <Box sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Preview Unavailable
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                {error?.includes('not found') 
                  ? 'The original file is not available in cloud storage. This may be an older document that was processed before file storage was enabled.'
                  : error || 'Could not load document preview.'}
              </Typography>
            </Box>
          )}

          {/* Fallback: Show extracted data when file is unavailable */}
          {!loading && !error && fallbackData && (
            <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={600}>
                  Original file not in cloud storage — showing extracted data instead
                </Typography>
              </Alert>

              {/* Document Metadata */}
              <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                {fallbackData.status && (
                  <Chip label={`Status: ${fallbackData.status}`} size="small" color="primary" variant="outlined" />
                )}
                {fallbackData.delivery_number && (
                  <Chip label={`Delivery: ${fallbackData.delivery_number}`} size="small" color="secondary" variant="outlined" />
                )}
                {fallbackData.uploaded_at && (
                  <Chip label={`Uploaded: ${new Date(fallbackData.uploaded_at).toLocaleDateString()}`} size="small" variant="outlined" />
                )}
                {fallbackData.has_comparison && (
                  <Chip label="Has Comparison" size="small" color="success" variant="outlined" />
                )}
              </Box>

              {/* Extracted Fields Table */}
              {Object.keys(fallbackData.extracted_fields || {}).length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DataIcon fontSize="small" sx={{ color: '#8B5CF6' }} />
                    Extracted Fields
                  </Typography>
                  <TableContainer sx={{ 
                    mb: 3, 
                    borderRadius: 2, 
                    border: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: alpha('#8B5CF6', 0.08) }}>
                          <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 100 }}>Confidence</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(fallbackData.extracted_fields).map(([key, val]: [string, any]) => (
                          <TableRow key={key} hover>
                            <TableCell sx={{ fontWeight: 600, color: '#8B5CF6', textTransform: 'capitalize' }}>
                              {key.replace(/_/g, ' ')}
                            </TableCell>
                            <TableCell>{val?.value || '—'}</TableCell>
                            <TableCell>
                              {val?.confidence != null ? (
                                <Chip
                                  label={`${(val.confidence * 100).toFixed(0)}%`}
                                  size="small"
                                  sx={{
                                    backgroundColor: val.confidence >= 0.8 
                                      ? alpha('#10B981', 0.15) 
                                      : val.confidence >= 0.5 
                                        ? alpha('#F59E0B', 0.15) 
                                        : alpha('#EF4444', 0.15),
                                    color: val.confidence >= 0.8 ? '#059669' : val.confidence >= 0.5 ? '#D97706' : '#DC2626',
                                    fontWeight: 600,
                                  }}
                                />
                              ) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* OCR Text */}
              {fallbackData.ocr_text && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    📄 Raw OCR Text
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: alpha('#000', 0.04), 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    maxHeight: 300, 
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                  }}>
                    {fallbackData.ocr_text}
                  </Box>
                </>
              )}

              {/* No data at all */}
              {Object.keys(fallbackData.extracted_fields || {}).length === 0 && !fallbackData.ocr_text && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No extracted data available for this document.
                </Typography>
              )}
            </Box>
          )}

          {!loading && !error && previewUrl && (
            <>
              {isImage ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'auto',
                    p: 2,
                  }}
                >
                  <img
                    src={previewUrl}
                    alt={displayName}
                    onError={(e) => {
                      console.error('Image failed to load:', previewUrl);
                      setError('Failed to render image. The file may be corrupted or unsupported.');
                    }}
                    style={{
                      maxWidth: `calc(${zoom}vw - 100px)`,
                      maxHeight: `calc(${zoom * 0.7}vh)`,
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      transform: `rotate(${rotation}deg)`,
                      transition: 'transform 0.3s ease',
                      borderRadius: 8,
                      boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                    }}
                  />
                </Box>
              ) : isPdf ? (
                <iframe
                  src={previewUrl}
                  title={displayName}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <FileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Preview not available for this file type
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<OpenNewIcon />}
                    onClick={() => window.open(previewUrl, '_blank')}
                    sx={{ mt: 2 }}
                  >
                    Open File
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
